import {assistantStorageKey} from '@/components/code-editor/assistant-history'
import {subscribeAuthIoMessage, unsubscribeSchemaChannel, useSocketIo} from 'src/composables/useSocketIo'
import {useTabsStore} from 'src/stores/tabs.store'
import {useSchemaStore} from 'src/stores/schema.store'
import {useMenuStore} from 'src/stores/menu.store'
import {useAuthStore} from 'src/stores/auth.store'
import {useVersionsStore} from 'src/stores/versions.store'
import {useNotify} from "nvd-u/composables/Notifiy";
import {SchemaToDbml} from "src/components/code-editor/schemaToDbml.js";
import {FileHelper} from "@/helpers/file-helper";
import {tabHelper} from "@/helpers/tabHelper";
import {useConfirm} from "nvd-u/composables/Confirm";
import {FetchRequest} from "../helpers/fetch-request";
import {COLLABORATOR_STATES, useCollaborationPresenceStore} from "@/stores/collaboration-presence.store";
import {SortItemsCommand} from '@/commands/SortItemsCommand'

function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
}

/** Tear down previous editor auth listener before registering a new one (tab switch / remount). */
let unsubscribeEditorAuthIo: (() => void) | null = null
const schemasPendingRealtimeRefresh = new Set<string>()

function getAiAssistantStorageKey(tab) {
    return assistantStorageKey(tab?.schema, useAuthStore().user?.id)
}

function isStaleAiAssistantRequest(storageKey, requestId) {
    if (!requestId) return false
    try {
        const raw = localStorage.getItem(`${storageKey}-stale-request-ids`)
        const parsed = raw ? JSON.parse(raw) : []
        return Array.isArray(parsed) && parsed.includes(requestId)
    } catch (e) {
        return false
    }
}

function appendAiAssistantMessage(tab, message, status = 'completed') {
    if (!tab || (!message?.content && !message?.operations?.length)) return

    try {
        const storageKey = getAiAssistantStorageKey(tab)
        if (isStaleAiAssistantRequest(storageKey, message.request_id)) return
        const raw = localStorage.getItem(storageKey)
        const parsed = raw ? JSON.parse(raw) : []
        const messages = Array.isArray(parsed) ? parsed : []
        let payload = { ...message, status: status === 'streaming' ? 'running' : status }

        if (message.request_id) {
            const existingIndex = messages.findIndex(entry =>
                entry?.request_id === message.request_id
                && entry?.role === message.role
            )
            if (existingIndex !== -1) {
                payload = {
                    ...messages[existingIndex],
                    ...message
                }
                messages.splice(existingIndex, 1, payload)
            } else {
                messages.push(payload)
            }
        } else {
            if (messages.some(entry =>
                entry?.role === message.role
                && entry?.content === message.content
            )) {
                return
            }
            messages.push(payload)
        }
        payload.status = payload.variant === 'error' ? 'failed' : (status === 'streaming' ? 'running' : status)
        localStorage.setItem(storageKey, JSON.stringify(messages))
        window.dispatchEvent(new CustomEvent('ai-assistant-message', {
            detail: {
                storageKey,
                message: payload,
                status: payload.variant === 'error' ? 'failed' : status
            }
        }))
    } catch (e) {
        // ignore storage errors
    }
}

function getAiAssistantReplyContent(data) {
    const candidates = [
        data?.answer,
        data?.response,
        typeof data?.message === 'string' ? data.message : '',
        data?.content
    ]
    return candidates.find(value => typeof value === 'string' && value.trim())?.trim() || ''
}

function getAiAssistantRestoreVersionId(data) {
    return data?.restore_version_id || data?.revert_version_id || data?.previous_version_id || null
}

function parseSocketPayload(event) {
    try {
        const data = JSON.parse(event.data)
        if (!data || !data.message || !data.message.data?.type) return null
        return data.message.data
    } catch {
        return null
    }
}

function handleAiAssistantCompleted(data, tabs) {
    const tab = tabs.tabs.find(t => t.schema.id === data.schema_id)
    if (!tab) return
    const answer = getAiAssistantReplyContent(data)
    const operations = Array.isArray(data.operations) ? data.operations : []
    if (answer || data.dbml || operations.length) {
        appendAiAssistantMessage(tab, {
            role: 'assistant',
            content: answer || 'Applied assistant changes.',
            dbml: data.dbml || '',
            operations,
            applied: data.applied || null,
            request_id: data.request_id || null,
            restoreVersionId: getAiAssistantRestoreVersionId(data)
        })
    }
    if (data.dbml && tab.editor) {
        tab.editor.setValue(data.dbml, -1)
        tab.dbml = data.dbml
    }
}

function handleAiAssistantChunk(data, tabs) {
    const tab = tabs.tabs.find(t => t.schema.id === data.schema_id)
    if (!tab || !data.request_id || typeof data.chunk !== 'string' || !data.chunk.length) return

    const storageKey = getAiAssistantStorageKey(tab)
    let content = data.chunk

    try {
        const raw = localStorage.getItem(storageKey)
        const parsed = raw ? JSON.parse(raw) : []
        const messages = Array.isArray(parsed) ? parsed : []
        const existing = messages.find(entry =>
            entry?.role === 'assistant' && entry?.request_id === data.request_id
        )
        if (existing?.content) {
            content = `${existing.content}${data.chunk}`
        }
    } catch (e) {
        // ignore storage errors
    }

    appendAiAssistantMessage(tab, {
        role: 'assistant',
        content,
        request_id: data.request_id,
        operations: []
    }, 'streaming')
}

function handleAiAssistantFailed(data, tabs) {
    const tab = tabs.tabs.find(t => t.schema.id === data.schema_id)
    if (!tab) return
    appendAiAssistantMessage(tab, {
        role: 'assistant',
        content: data.error || 'Assistant request failed.',
        variant: 'error',
        request_id: data.request_id || null
    })
}

async function refreshSchemaAfterAssistantApply(tab) {
    if (!tab?.schema?.slug) return
    const schemaStore = useSchemaStore()
    try {
        const latestSchema = await schemaStore.getSchemaData(tab.schema.slug)
        if (!latestSchema) return
        tab.schema = latestSchema
        await new SchemaToDbml(tab).convert()
    } catch (e) {
        // ignore refresh errors and keep existing optimistic UI state
    }
}

async function handleAiAssistantApplied(data, tabs) {
    const tab = tabs.tabs.find(t => t.schema.id === data.schema_id)
    if (!tab) return
    await refreshSchemaAfterAssistantApply(tab)
    window.dispatchEvent(new CustomEvent('ai-assistant-applied', {
        detail: {
            storageKey: getAiAssistantStorageKey(tab),
            request_id: data.request_id,
            applied: data.applied || null
        }
    }))
}

function handleAiAssistantRejected(data, tabs) {
    const tab = tabs.tabs.find(t => t.schema.id === data.schema_id)
    if (!tab) return
    window.dispatchEvent(new CustomEvent('ai-assistant-rejected', {
        detail: {
            storageKey: getAiAssistantStorageKey(tab),
            request_id: data.request_id,
            dismissed: data.dismissed === true,
            dismissed_at: data.dismissed_at || null
        }
    }))
}

function isUnauthenticated(updated_by) {
    const auth = useAuthStore()
    return !auth.user || (auth.user.id === updated_by)
}

function isSchemaPreviewing() {
    return useVersionsStore().isPreviewing
}

function queueRealtimeRefresh(schemaId) {
    if (schemaId) schemasPendingRealtimeRefresh.add(String(schemaId))
}

async function refreshQueuedRealtimeSchemas() {
    if (!schemasPendingRealtimeRefresh.size) return
    const pendingSchemaIds = [...schemasPendingRealtimeRefresh]
    schemasPendingRealtimeRefresh.clear()
    const tabs = useTabsStore()
    for (const schemaId of pendingSchemaIds) {
        const tab = tabs.findTabBySchemaId(schemaId)
            || tabs.tabs.find(t => String(t.schema?.slug) === String(schemaId))
        if (tab) await refreshSchemaAfterAssistantApply(tab)
    }
}

if (typeof window !== 'undefined') {
    window.addEventListener('ai-assistant-preview-closed', () => {
        void refreshQueuedRealtimeSchemas()
    })
    window.addEventListener('schema-version-preview-closed', () => {
        void refreshQueuedRealtimeSchemas()
    })
}

function getCurrentTab(schemaId) {
    const tabs = useTabsStore()
    return tabs.findTabBySchemaId(schemaId)
}

function isActiveSchemaTab(currentTab) {
    const tabs = useTabsStore()
    return !!currentTab && tabs.selectedTab?.id === currentTab.id
}

function findTableByPayload(currentTab, item) {
    if (!currentTab || item?.resource !== 'table') return null
    return currentTab.schema.schema_data.tables.find(table =>
        table.p_id === item.p_id || (item.name && table.name === item.name)
    ) || null
}

function collaboratorUserFromSchema(currentTab, userId) {
    const member = currentTab?.schema?.members?.find(item => String(item.id) === String(userId))
    if (!member) return { id: userId }
    return {
        id: member.id,
        name: member.name || member.email,
        initials: member.initials,
        avatar: member.avatar,
        email: member.email
    }
}

function recordRecentTableActivity(currentTab, updatedBy, items = []) {
    if (!currentTab || isUnauthenticated(updatedBy)) return
    const presence = useCollaborationPresenceStore()
    for (const item of Array.isArray(items) ? items : []) {
        const table = findTableByPayload(currentTab, item)
        if (!table?.p_id) continue
        presence.receiveActivity({
            type: 'collaborator.activity_changed',
            schema_id: currentTab.schema?.id || currentTab.id,
            user: collaboratorUserFromSchema(currentTab, updatedBy),
            target: {
                resource: 'table',
                p_id: table.p_id
            },
            state: COLLABORATOR_STATES.RECENT,
            occurred_at: new Date().toISOString()
        })
    }
}

function findFieldTarget(currentTab, field) {
    if (!currentTab || !field) return { table: null, field: null }
    const table = currentTab.schema.schema_data.tables.find(item =>
        item.p_id === field.parent_id || item.fields?.some(existingField =>
            existingField.p_id === field.p_id || (field.name && existingField.name === field.name)
        )
    )
    if (!table) return { table: null, field: null }
    const tableField = table.fields.find(existingField =>
        existingField.p_id === field.p_id || (field.name && existingField.name === field.name)
    ) || null
    return { table, field: tableField }
}

function recordRecentFieldActivity(currentTab, updatedBy, fields = []) {
    if (!currentTab || isUnauthenticated(updatedBy)) return
    const presence = useCollaborationPresenceStore()
    for (const rawField of Array.isArray(fields) ? fields : []) {
        const { table } = findFieldTarget(currentTab, rawField)
        if (!table?.p_id) continue
        presence.receiveActivity({
            type: 'collaborator.activity_changed',
            schema_id: currentTab.schema?.id || currentTab.id,
            user: collaboratorUserFromSchema(currentTab, updatedBy),
            target: {
                resource: 'table',
                p_id: table.p_id
            },
            state: COLLABORATOR_STATES.RECENT,
            occurred_at: new Date().toISOString()
        })
    }
}

async function highlightRealtimeTables(currentTab, items = []) {
    if (isSchemaPreviewing() || !isActiveSchemaTab(currentTab) || !currentTab?.canvas) return
    const seen = new Set()
    for (const item of Array.isArray(items) ? items : []) {
        const table = findTableByPayload(currentTab, item)
        if (!table || seen.has(table.p_id || table.name)) continue
        seen.add(table.p_id || table.name)
        await currentTab.canvas.locateItem(table, 1200)
        await sleep(250)
    }
}

async function highlightRealtimeFields(currentTab, fields = []) {
    if (isSchemaPreviewing() || !isActiveSchemaTab(currentTab) || !currentTab?.canvas) return
    const seen = new Set()
    for (const rawField of Array.isArray(fields) ? fields : []) {
        const { table, field } = findFieldTarget(currentTab, rawField)
        if (!table || !field) continue
        const key = `${table.p_id || table.name}:${field.p_id || field.name}`
        if (seen.has(key)) continue
        seen.add(key)
        field.left = table.left
        field.top = table.top
        await currentTab.canvas.locateItem(field, 1200)
        await sleep(250)
    }
}

function updateItems(items, updated_by, schemaId, force = false) {
    if (isSchemaPreviewing()) {
        queueRealtimeRefresh(schemaId)
        return
    }
    const tab = useTabsStore()
    if (!force && isUnauthenticated(updated_by)) return
    let currentTab = tab.findTabBySchemaId(schemaId)
    if (!currentTab) return
    items.forEach(item => {
        let existing
        existing = item.resource === 'table' ? currentTab.schema.schema_data.tables.find(t => t.p_id === item.p_id) :
            currentTab.schema.schema_data.notes.find(n => n.p_id === item.p_id)
        if (existing) {
            for (const [key, value] of Object.entries(item)) {
                existing[key] = value
            }
        } else {
            item.resource === 'table' ?
                currentTab.schema.schema_data.tables.push(item)
                :
                currentTab.schema.schema_data.notes.push(item)
        }
    })
    new SchemaToDbml(currentTab).convert()
}

function removeItems(items, updated_by, schemaId, force = false) {
    if (isSchemaPreviewing()) {
        queueRealtimeRefresh(schemaId)
        return
    }
    const tab = useTabsStore()
    if (!force && isUnauthenticated(updated_by)) return
    let currentTab = tab.findTabBySchemaId(schemaId)
    if (!currentTab) return
    items.forEach(item => {
        let itemIndex
        if (item.resource === 'table') {
            itemIndex = currentTab.schema.schema_data.tables.findIndex(t => t.p_id === item.p_id)
            if (itemIndex !== -1)
                currentTab.schema.schema_data.tables.splice(itemIndex, 1)
        } else if (item.resource === 'note') {
            itemIndex = currentTab.schema.schema_data.notes.findIndex(n => n.p_id === item.p_id)
            if (itemIndex !== -1)
                currentTab.schema.schema_data.notes.splice(itemIndex, 1)
        }
        if (itemIndex === -1) return
    })
    new SchemaToDbml(currentTab).convert()
}

function updateSchema(schema, updated_by) {
    if (isSchemaPreviewing()) {
        queueRealtimeRefresh(schema?.id || schema?.slug)
        return
    }
    const tab = useTabsStore()
    if (isUnauthenticated(updated_by)) return
    let selectedTab = tab.tabs.find(t => t.schema.slug === schema.slug)
    if (!selectedTab) return
    for (const [key, value] of Object.entries(schema)) {
        selectedTab.schema[key] = value
    }
}

function updateSorting(items, updated_by, schemaId) {
    if (isSchemaPreviewing()) {
        queueRealtimeRefresh(schemaId)
        return
    }
    const tab = useTabsStore()
    if (isUnauthenticated(updated_by)) return
    let currentTab = tab.findTabBySchemaId(schemaId)
    if (!currentTab) return
    items.forEach(item => {
        let existing
        existing = item.resource === 'table' ? currentTab.schema.schema_data.tables.find(t => t.p_id === item.p_id) :
            currentTab.schema_data.notes.find(n => n.p_id === item.p_id)
        if (existing) {
            existing.top = item.top
            existing.left = item.left
        }
    })
}

function updateFields(fields, updated_by, schemaId, force = false) {
    if (isSchemaPreviewing()) {
        queueRealtimeRefresh(schemaId)
        return
    }
    const tab = useTabsStore()
    if (!force && isUnauthenticated(updated_by)) return

    let currentTab = tab.findTabBySchemaId(schemaId)
    if (!currentTab) return
    fields.forEach(field => {
        let table = currentTab.schema.schema_data.tables.find(t => t.p_id === field.parent_id)
        if (table) {
            let existingField = table.fields.find(f => f.p_id === field.p_id)
            if (!existingField) return table.fields.push(field)
            for (const [key, value] of Object.entries(field)) {
                existingField[key] = value
            }
            // if any field is checked, table is checked
            // if all fields are unchecked, table is unchecked
            table.checked = !!table.fields.find(f => f.checked);
        }
    })
}

function removeFields(fields, updated_by, schemaId, force = false) {
    if (isSchemaPreviewing()) {
        queueRealtimeRefresh(schemaId)
        return
    }
    const tab = useTabsStore()
    if (!force && isUnauthenticated(updated_by)) return

    let currentTab = tab.findTabBySchemaId(schemaId)
    if (!currentTab) return
    fields.forEach(field => {
        let table = currentTab.schema.schema_data.tables.find(t => t.p_id === field.parent_id)
        if (!table) return
        let fieldIndex = table.fields.findIndex(f => f.p_id === field.p_id)
        if (fieldIndex === -1) return
        table.fields.splice(fieldIndex, 1)
        table.checked = !!table.fields.find(f => f.checked);
    })
}

function connectUser(data) {
    const auth = useAuthStore()
    const tab = useTabsStore()
    const notify = useNotify()
    let currentTab = tab.findTabBySchemaId(data.schema_id)
    if (!currentTab) return
    currentTab.schema?.members?.forEach(member => {
        let idx = data.activeUsers.findIndex(m => m.id === member.id)
        if (idx !== -1) {
            if (member.id !== auth.user.id && tab.selectedTab.id === currentTab.id)
                notify.success(`Member Joined`, `${member.name || member.email} just joined live collaboration`)
            Object.keys(member).forEach(key => {
                member[key] = data.activeUsers[idx][key]
            })
        }
    })
}

function disconnectUser(data) {
    const auth = useAuthStore()
    const tab = useTabsStore()
    const notify = useNotify()
    let currentTab = tab.findTabBySchemaId(data.schema_id)
    if (!currentTab) return
    currentTab.schema?.members?.forEach(member => {
        let idx = data.inactiveUsers.findIndex(m => m.id === member.id)
        if (idx !== -1) {
            if (member.id !== auth.user.id && tab.selectedTab.id === currentTab.id)
                notify.success(`Member Left`, `${member.name || member.email} just left live collaboration`)
            Object.keys(member).forEach(key => {
                member[key] = data.inactiveUsers[idx][key]
            })
        }
    })
}

function updateMembers(data) {
    const tab = useTabsStore()
    const auth = useAuthStore()
    let currentTab = tab.findTabBySchemaId(data.schema_id)
    if (!currentTab) return
    if (!data.members_list.find(m => m.id === auth.user.id)) {
        unsubscribeSchemaChannel(currentTab.id)
        return
    }
    return currentTab.schema.members = data.members_list
}

export function onEditorEvents(selectedTab) {
    let io = useSocketIo(selectedTab.id).io
    const auth = useAuthStore()
    const schema = useSchemaStore()
    const menu = useMenuStore()
    const notify = useNotify()
    const tabs = useTabsStore()
    if (!auth.isLoggedIn) return () => {}

    unsubscribeEditorAuthIo?.()
    unsubscribeEditorAuthIo = null

    const unsubAuthThis = subscribeAuthIoMessage(async event => {
        const data = parseSocketPayload(event)
        if (!data) return

        switch (data.type) {
            case 'ai_assistant.started':
                break
            case 'ai_assistant.chunk':
                handleAiAssistantChunk(data, tabs)
                break
            case 'ai_assistant.completed':
                handleAiAssistantCompleted(data, tabs)
                break
            case 'ai_assistant.failed':
                handleAiAssistantFailed(data, tabs)
                break
            case 'ai_assistant.applied':
                await handleAiAssistantApplied(data, tabs)
                break
            case 'ai_assistant.rejected':
                handleAiAssistantRejected(data, tabs)
                break
        }
    })
    unsubscribeEditorAuthIo = unsubAuthThis

    io.onmessage = async event => {
        let data = parseSocketPayload(event)
        if (!data) return
        switch (data.type) {
            case 'update note':
            case 'update table':
            case 'update resources': {
                const currentTab = getCurrentTab(data.schema_id)
                updateItems(data.schema, data.updated_by, data.schema_id)
                recordRecentTableActivity(currentTab, data.updated_by, data.schema)
                if (!isUnauthenticated(data.updated_by)) {
                    await highlightRealtimeTables(currentTab, data.schema)
                }
                break;
            }
            case 'remove resource': {
                removeItems(data.schema, data.updated_by, data.schema_id)
                break;
            }
            case 'update schema': {
                updateSchema(data.schema, data.updated_by)
                break;
            }
            case 'update sorting': {
                updateSorting(data.schema, data.updated_by, data.schema_id)
                break;
            }
            case 'update field': {
                const currentTab = getCurrentTab(data.schema_id)
                updateFields(data.schema, data.updated_by, data.schema_id)
                recordRecentFieldActivity(currentTab, data.updated_by, data.schema)
                if (!isUnauthenticated(data.updated_by)) {
                    await highlightRealtimeFields(currentTab, data.schema)
                }
                break;
            }
            case 'collaborator.cursor_moved': {
                useCollaborationPresenceStore().receiveCursor(data)
                break;
            }
            case 'collaborator.activity_changed': {
                useCollaborationPresenceStore().receiveActivity(data)
                break;
            }
            case 'collaborator.activity_cleared': {
                useCollaborationPresenceStore().clearActivity(data)
                break;
            }
            case 'memberJoined': {
                connectUser(data)
                break;
            }
            case 'memberLeft': {
                disconnectUser(data)
                break;
            }
            case 'collaborator_updated': {
                updateMembers(data)
                break;
            }
            case 'ai template': {
                schema.aiForm.loading = false
                menu.toggleCreateNewSchemaModalOpen(false)
                const existingTab = tabs.tabs.find(t => t.schema.id === data.id)
                const tab = existingTab || {schema: data}
                tab.isGeneratingTemplate = true
                if (!existingTab) {
                    schema.openNewTab(tab)
                } else {
                    for (const [key, value] of Object.entries(data)) {
                        existingTab.schema[key] = value
                    }
                }
                await new SchemaToDbml(tab).convert()
                await new SortItemsCommand(tab).execute()
                break;
            }

            case 'ai import' : {
                let tab = tabs.tabs.find(t => t.schema.id === data.id)
                if (!tab) return

                tab.isImporting = true

                for (const [key, value] of Object.entries(data)) {
                    tab.schema[key] = value
                }

                tab.canvas.getItemPositionsForTables(tab.schema.schema_data.tables)

                new SchemaToDbml(tab).convert()
                schema.importLoading = false
                menu.toggleImportSqlModal(false)

                break;
            }

            case 'ai complete': {
                let tab = tabs.tabs.find(t => t.schema.id === data.id)
                if (!tab) return

                delete tab['isImporting']

                for (const [key, value] of Object.entries(data)) {
                    tab.schema[key] = value
                }

                let updatedPositions
                if (tab.isGeneratingTemplate) {
                    await new SortItemsCommand(tab).execute()
                    updatedPositions = tab.schema.schema_data.tables.concat(tab.schema.schema_data.notes)
                        .map(({p_id, resource, top, left}) => ({p_id, resource, top, left}))
                    delete tab.isGeneratingTemplate
                } else {
                    updatedPositions = tab.canvas.getItemPositionsForTables(tab.schema.schema_data.tables)
                }
                let sortItemsReq = new FetchRequest(`schemas/${tab.schema.slug}/sort_items`, 'POST')
                sortItemsReq.config.body = JSON.stringify({
                    data: updatedPositions
                })
                await sortItemsReq.send().catch(() => tab.unsaved = true)

                new SchemaToDbml(tab).convert()
                // const confirm = await useConfirm('Ai Generation Completed', 'Would you like to auto sort the entire project right now?')
                // if (confirm) {
                notify.success('Ai Generation Completed', 'You can continue your work')
                schema.importLoading = false
                menu.toggleImportSqlModal(false)
                // }
                break;
            }

        }
    }

    return () => {
        unsubAuthThis()
        if (unsubscribeEditorAuthIo === unsubAuthThis) {
            unsubscribeEditorAuthIo = null
        }
    }
}
